<template>
  <div>
    <v-container class="container-header">
      <v-card
          color="background"
          class="header border-0"
          elevation="0"
          rounded="0"
      >
        <NuxtLink :to="isIframe ? '#' : '/'" class="mx-3 d-flex align-items-center" aria-label="logo">
          <div class="logo"></div>
        </NuxtLink>
        <v-spacer></v-spacer>

        <NuxtLink to="/faq" target="_blank" class="text-decoration-none color-contrast" aria-label="faq">
          <v-btn
              color="gray-dark"
              min-width="0"
              size="small"
              variant="flat"
              rounded="lg"
              icon="mdi-help-circle"
          >
          </v-btn>
        </NuxtLink>
      </v-card>
    </v-container>

  </div>
</template>

<script lang="ts">
import {defineComponent, onMounted, onBeforeMount, onUnmounted, computed, ref} from 'vue';
import {useAuthStore} from '~/plugins/store/auth';
import {useTranslationStore, translation} from '~/plugins/store/translations';
import {useRouter, useRoute} from 'vue-router';
import type {MenuItem, TranslationList} from "~/types/Utils";
import {reloadNuxtApp} from "nuxt/app";
import {useTheme} from 'vuetify';
import {useProductStore} from "~/plugins/store/product";
import type {ProductStatuses} from "~/types/Product";
import {useSystemStore} from "~/plugins/store/system";

export default defineComponent({
  setup() {
    const authStore = useAuthStore();
    const user = computed(() => authStore.getUser());

    const productStore = useProductStore();
    const productStatuses = ref<ProductStatuses>(productStore.productStatuses);

    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference
    const mainMenuItems = ref<MenuItem[]>([]);

    const route = useRoute();

    const menuOpen = ref(false);
    const isOpen = ref(true);
    const systemStore = useSystemStore();
    const isDarkTheme = ref(systemStore.isDarkTheme);
    const isIframe = systemStore.isIframe;

    const theme = useTheme();

    //Loading translations when component is mounted
    onMounted(async () => {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches && !systemStore.isThemeChangedManually) {
        systemStore.isDarkTheme = true;
        isDarkTheme.value = true;
        theme.global.name.value = 'xigaDarkTheme';
      } else {
        theme.global.name.value = systemStore.isDarkTheme ? 'xigaDarkTheme' : 'xigaTheme';
      }

      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', event => {
        if (!systemStore.isThemeChangedManually) {
          systemStore.isDarkTheme = event.matches;
          theme.global.name.value = systemStore.isDarkTheme ? 'xigaDarkTheme' : 'xigaTheme';
        }
      });


      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      const subMenuItems = [];
      if (productStatuses.value.is_rca_available) {
        subMenuItems.push({
          title: translation(translationList.value.rca, 'RCA'),
          path: "/rca"
        });
      }
      if (productStatuses.value.is_house_insurance_available) {
        subMenuItems.push({
          title: translation(translationList.value.home_insurance, 'Asigurare de locuință'),
          path: "/house"
        });
      }
      if (productStatuses.value.is_travel_available) {
        subMenuItems.push({
          title: translation(translationList.value.travel_insurance, 'Asigurare de călătorie'),
          path: "/travel"
        });
      }

      mainMenuItems.value = [
        {
          title: translation(translationList.value.home, 'Despre XIGA'),
          path: "/info/about-us"
        },
        {
          title: translation(translationList.value.products, 'Calcul RCA'),
          path: "/rca",
        },
        {
          title: translation(translationList.value.about_us, 'Asigurare de călătorie'),
          path: "/travel"
        },
        {
          title: translation(translationList.value.careers, 'Contactează-ne'),
          path: "/contact-us"
        },
      ];
    });

    //Define menu with account menu items
    const accountMenuItems = ref([
      {
        title: translation(translationList.value.my_profile, 'Profil'),
        icon: "mdi-account",
        path: "/account/profile"
      },
      // {
      //   title: translation(translationList.value.history, 'Istoric'),
      //   icon: "mdi-history",
      //   path: ""
      // },
    ]);

    //Marking the menu buttons and menu dropdown buttons as active
    const isBtnActive = (path: String, subMenuItems = []) => {
      // Check if the current path matches the defined path for the menu
      if (path && route.path === path) {
        return true;
      }
      // Check if the current path matches any of the submenu paths
      return subMenuItems.some(subItem => route.path === subItem.path);
    };

    //We need the width of the screen in order to change the account button style
    const width = ref(0);
    const isWideScreen = computed(() => width.value > 600);

    const handleResize = () => {
      if (typeof window !== 'undefined') {
        width.value = window.innerWidth;
      }
    };

    onMounted(() => {
      handleResize(); // Set initial width
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    const logout = async () => {
      await authStore.logout();
      reloadNuxtApp();
    };

    return {
      user,
      translationList,
      mainMenuItems,
      accountMenuItems,
      translation,
      isBtnActive,
      isWideScreen,
      logout,
      productStatuses,
      menuOpen,
      isOpen,
      systemStore,
      isIframe
    };
  },
});
</script>

<style scoped>

</style>
